import _defineProperty from "/app/workspaces/v_1/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { UploaderBlock } from '../../abstract/UploaderBlock.js';
import { ActivityBlock } from '../../abstract/ActivityBlock.js';
import { canUsePermissionsApi } from '../utils/abilities.js';
import { debounce } from '../utils/debounce.js';
import { UploadSource } from '../utils/UploadSource.js';
export class CameraSource extends UploaderBlock {
  constructor() {
    super(...arguments);
    _defineProperty(this, "couldBeCtxOwner", true);
    _defineProperty(this, "activityType", ActivityBlock.activities.CAMERA);
    /** @private */
    _defineProperty(this, "_unsubPermissions", null);
    _defineProperty(this, "init$", _objectSpread(_objectSpread({}, this.init$), {}, {
      video: null,
      videoTransformCss: null,
      shotBtnDisabled: true,
      videoHidden: true,
      messageHidden: true,
      requestBtnHidden: canUsePermissionsApi(),
      l10nMessage: null,
      originalErrorMessage: null,
      cameraSelectOptions: null,
      cameraSelectHidden: true,
      onCameraSelectChange: e => {
        /** @type {String} */
        this._selectedCameraId = e.target.value;
        this._capture();
      },
      onCancel: () => {
        this.historyBack();
      },
      onShot: () => {
        this._shot();
      },
      onRequestPermissions: () => {
        this._capture();
      }
    }));
    /** @private */
    _defineProperty(this, "_onActivate", () => {
      if (canUsePermissionsApi()) {
        this._subscribePermissions();
      }
      this._capture();
    });
    /** @private */
    _defineProperty(this, "_onDeactivate", () => {
      if (this._unsubPermissions) {
        this._unsubPermissions();
      }
      this._stopCapture();
    });
    /** @private */
    _defineProperty(this, "_handlePermissionsChange", () => {
      this._capture();
    });
    /**
     * @private
     * @param {'granted' | 'denied' | 'prompt'} state
     */
    _defineProperty(this, "_setPermissionsState", debounce(state => {
      this.$.originalErrorMessage = null;
      this.classList.toggle('initialized', state === 'granted');
      if (state === 'granted') {
        this.set$({
          videoHidden: false,
          shotBtnDisabled: false,
          messageHidden: true
        });
      } else if (state === 'prompt') {
        this.$.l10nMessage = this.l10n('camera-permissions-prompt');
        this.set$({
          videoHidden: true,
          shotBtnDisabled: true,
          messageHidden: false
        });
        this._stopCapture();
      } else {
        this.$.l10nMessage = this.l10n('camera-permissions-denied');
        this.set$({
          videoHidden: true,
          shotBtnDisabled: true,
          messageHidden: false
        });
        this._stopCapture();
      }
    }, 300));
  }
  /** @private */
  async _subscribePermissions() {
    try {
      // @ts-ignore
      let permissionsResponse = await navigator.permissions.query({
        name: 'camera'
      });
      permissionsResponse.addEventListener('change', this._handlePermissionsChange);
    } catch (err) {
      console.log('Failed to use permissions API. Fallback to manual request mode.', err);
      this._capture();
    }
  }

  /** @private */
  async _capture() {
    let constr = {
      video: {
        width: {
          ideal: 1920
        },
        height: {
          ideal: 1080
        },
        frameRate: {
          ideal: 30
        }
      },
      audio: false
    };
    if (this._selectedCameraId) {
      constr.video.deviceId = {
        exact: this._selectedCameraId
      };
    }
    /** @private */
    this._canvas = document.createElement('canvas');
    /** @private */
    this._ctx = this._canvas.getContext('2d');
    try {
      this._setPermissionsState('prompt');
      let stream = await navigator.mediaDevices.getUserMedia(constr);
      stream.addEventListener('inactive', () => {
        this._setPermissionsState('denied');
      });
      this.$.video = stream;
      /** @private */
      this._capturing = true;
      this._setPermissionsState('granted');
    } catch (err) {
      this._setPermissionsState('denied');
      this.$.originalErrorMessage = err.message;
    }
  }

  /** @private */
  _stopCapture() {
    if (this._capturing) {
      var _this$$$video;
      (_this$$$video = this.$.video) === null || _this$$$video === void 0 || _this$$$video.getTracks()[0].stop();
      this.$.video = null;
      this._capturing = false;
    }
  }

  /** @private */
  _shot() {
    this._canvas.height = this.ref.video['videoHeight'];
    this._canvas.width = this.ref.video['videoWidth'];
    // @ts-ignore
    this._ctx.drawImage(this.ref.video, 0, 0);
    const date = Date.now();
    const name = "camera-".concat(date, ".jpeg");
    const format = 'image/jpeg';
    this._canvas.toBlob(blob => {
      let file = new File([blob], name, {
        lastModified: date,
        type: format
      });
      this.addFileFromObject(file, {
        source: UploadSource.CAMERA
      });
      this.set$({
        '*currentActivity': ActivityBlock.activities.UPLOAD_LIST
      });
    }, format);
  }
  async initCallback() {
    super.initCallback();
    this.registerActivity(this.activityType, {
      onActivate: this._onActivate,
      onDeactivate: this._onDeactivate
    });
    this.subConfigValue('cameraMirror', val => {
      this.$.videoTransformCss = val ? 'scaleX(-1)' : null;
    });
    try {
      let deviceList = await navigator.mediaDevices.enumerateDevices();
      let cameraSelectOptions = deviceList.filter(info => {
        return info.kind === 'videoinput';
      }).map((info, idx) => {
        return {
          text: info.label.trim() || "".concat(this.l10n('caption-camera'), " ").concat(idx + 1),
          value: info.deviceId
        };
      });
      if (cameraSelectOptions.length > 1) {
        this.$.cameraSelectOptions = cameraSelectOptions;
        this.$.cameraSelectHidden = false;
      }
    } catch (err) {
      // mediaDevices isn't available for HTTP
      // TODO: handle this case
    }
  }
}
CameraSource.template = /* HTML */"\n  <lr-activity-header>\n    <button type=\"button\" class=\"mini-btn\" set=\"onclick: *historyBack\">\n      <lr-icon name=\"back\"></lr-icon>\n    </button>\n    <div set=\"@hidden: !cameraSelectHidden\">\n      <lr-icon name=\"camera\"></lr-icon>\n      <span l10n=\"caption-camera\"></span>\n    </div>\n    <lr-select\n      class=\"camera-select\"\n      set=\"$.options: cameraSelectOptions; @hidden: cameraSelectHidden; onchange: onCameraSelectChange\"\n    >\n    </lr-select>\n    <button type=\"button\" class=\"mini-btn close-btn\" set=\"onclick: *closeModal\">\n      <lr-icon name=\"close\"></lr-icon>\n    </button>\n  </lr-activity-header>\n  <div class=\"content\">\n    <video\n      autoplay\n      playsinline\n      set=\"srcObject: video; style.transform: videoTransformCss; @hidden: videoHidden\"\n      ref=\"video\"\n    ></video>\n    <div class=\"message-box\" set=\"@hidden: messageHidden\">\n      <span>{{l10nMessage}}</span>\n      <span>{{originalErrorMessage}}</span>\n      <button\n        type=\"button\"\n        set=\"onclick: onRequestPermissions; @hidden: requestBtnHidden\"\n        l10n=\"camera-permissions-request\"\n      ></button>\n    </div>\n    <button type=\"button\" class=\"shot-btn\" set=\"onclick: onShot; @disabled: shotBtnDisabled\">\n      <lr-icon name=\"camera\"></lr-icon>\n    </button>\n  </div>\n";